import { Check, Star } from 'lucide-react'
import type { PricingPlan } from '../types'

const Pricing = () => {
  const plans: PricingPlan[] = [
    {
      id: 'starter',
      name: 'Starter Plan',
      price: '$150',
      period: '/month',
      description: 'Perfect for small businesses getting started with digital marketing',
      features: [
        'SEO Optimization',
        'Social Media Management',
        'Content Creation',
        'Monthly Performance Reports',
        'Email Support',
        'Basic Analytics Dashboard'
      ],
      ctaText: 'Get Started'
    },
    {
      id: 'growth',
      name: 'Growth Plan',
      price: '$300',
      period: '/month',
      description: 'Ideal for growing businesses ready to scale their marketing efforts',
      features: [
        'Everything in Starter',
        'PPC Campaign Management',
        'Email Marketing Automation',
        'Advanced Analytics & Reporting',
        'Conversion Rate Optimization',
        'Priority Phone Support',
        'Bi-weekly Strategy Calls',
        'A/B Testing & Optimization'
      ],
      popular: true,
      ctaText: 'Start Growing'
    },
    {
      id: 'advanced',
      name: 'Advanced Plan',
      price: 'Custom',
      period: ' Pricing',
      description: 'Comprehensive solution for enterprises requiring full-service marketing',
      features: [
        'Everything in Growth',
        'Custom Strategy Development',
        'Dedicated Account Manager',
        'Multi-Channel Campaign Management',
        'Advanced Marketing Automation',
        '24/7 Priority Support',
        'Weekly Strategy Sessions',
        'Custom Integrations',
        'White-label Reporting'
      ],
      ctaText: 'Contact Sales'
    }
  ]

  const scrollToContact = () => {
    const element = document.querySelector('#contact')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section id="pricing" className="section-spacing bg-gray-50">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that best fits your business needs. All plans include 
            our core services with no hidden fees or long-term contracts.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-sm border-2 transition-all duration-300 hover:shadow-lg ${
                plan.popular 
                  ? 'border-primary scale-105 lg:scale-110' 
                  : 'border-gray-200 hover:border-primary-200'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-primary text-white px-6 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                    <Star size={16} className="fill-current" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {plan.description}
                  </p>
                  
                  {/* Price */}
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl lg:text-5xl font-bold text-gray-900">
                      {plan.price}
                    </span>
                    <span className="text-xl text-gray-600">
                      {plan.period}
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <ul className="space-y-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={14} className="text-primary" />
                        </div>
                        <span className="text-gray-700">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <button
                  onClick={scrollToContact}
                  className={`w-full py-4 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                    plan.popular
                      ? 'bg-primary text-white hover:bg-primary-600'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {plan.ctaText}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not sure which plan is right for you?
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation with our digital marketing experts. We'll analyze 
              your current situation and recommend the best strategy for your business goals.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={scrollToContact}
                className="btn-primary px-8 py-4"
              >
                Schedule Free Consultation
              </button>
              <button className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-primary hover:text-primary transition-colors duration-200">
                View Detailed Comparison
              </button>
            </div>
          </div>
        </div>

        {/* Guarantee */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-primary-50 text-primary px-6 py-3 rounded-full">
            <Check size={20} />
            <span className="font-semibold">30-Day Money-Back Guarantee</span>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Pricing